import {
  add,
  addMilliseconds,
  differenceInMilliseconds,
  Duration,
  endOfDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isValid,
  startOfDay,
} from 'date-fns';
import { Interval } from './interval';
import { memoize } from 'lodash';
import { dateResolver } from './date-resolvers';

function isValidIntervalResolver(
  interval: Partial<Interval>,
  options: { mustHaveSomeDuration?: boolean } = {},
): string {
  return `${dateResolver(interval?.start)}-${dateResolver(interval?.end)}-${options.mustHaveSomeDuration}`;
}

function isValidIntervalImpl(
  interval: Partial<Interval>,
  { mustHaveSomeDuration }: { mustHaveSomeDuration?: boolean } = {},
): boolean {
  const { start, end } = interval ?? {};

  return (
    start != null &&
    end != null &&
    isValid(start) &&
    isValid(end) &&
    (!isBefore(end, start) ||
      (!!mustHaveSomeDuration && isAfter(end as Date, start as Date)))
  );
}

export const isValidInterval = memoize(
  isValidIntervalImpl,
  isValidIntervalResolver,
);

function isSameIntervalResolver(
  intervalLeft: Partial<Interval>,
  intervalRight: Partial<Interval>,
  options: { sameDayOnly?: boolean } = {},
): string {
  return `${dateResolver(intervalLeft?.start)}-${dateResolver(intervalLeft?.end)}-${dateResolver(intervalRight?.start)}-${dateResolver(intervalRight?.end)}-${options.sameDayOnly}`;
}

function isSameIntervalImpl(
  intervalLeft: Partial<Interval>,
  intervalRight: Partial<Interval>,
  { sameDayOnly = false } = {},
): boolean {
  if (!isValidInterval(intervalLeft) || !isValidInterval(intervalRight)) {
    return false;
  }

  const compareFn = sameDayOnly ? isSameDay : isEqual;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startDateMatches = compareFn(intervalLeft.start!, intervalRight.start!);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const endDateMatches = compareFn(intervalLeft.end!, intervalRight.end!);

  return startDateMatches && endDateMatches;
}

export const isSameInterval = memoize(
  isSameIntervalImpl,
  isSameIntervalResolver,
);

function dateTupleToIntervalResolver([start, end]: [Date, Date]): string {
  return `${dateResolver(start)}-${dateResolver(end)}`;
}

function dateTupleToIntervalImpl([start, end]: [Date, Date]): Interval {
  return { start, end };
}

export const dateTupleToInterval = memoize(
  dateTupleToIntervalImpl,
  dateTupleToIntervalResolver,
);

function toStartAndEndOfIntervalDaysResolver(interval: Interval): string {
  return `${dateResolver(interval.start)}-${dateResolver(interval.end)}`;
}

function toStartAndEndOfIntervalDaysImpl(interval: Interval): Interval {
  return {
    start: startOfDay(interval.start),
    end: endOfDay(interval.end),
  };
}

export const toStartAndEndOfIntervalDays = memoize(
  toStartAndEndOfIntervalDaysImpl,
  toStartAndEndOfIntervalDaysResolver,
);

/**
 * Creates an Interval starting at the beginning of the given day and ending
 * after the specified duration (defaults to 1 day).
 *
 * @example
 * // Single-day interval (Default duration of 1 day)
 * dateToInterval(new Date('2025-06-01T14:00:00'))
 * // → { start: 2025-06-01T00:00:00, end: 2025-06-02T00:00:00 }
 *
 * // Two-day interval
 * dateToInterval(new Date('2025-06-01'), { days: 2 })
 * // → { start: 2025-06-01T00:00:00, end: 2025-06-03T00:00:00 }
 */
export function dateToInterval(
  date: Date,
  duration: Duration = { days: 1 },
): Interval {
  const start = startOfDay(date);
  return { start, end: add(start, duration) };
}

/** Midpoint of `interval`, e.g. 08:00–16:00 → 12:00. */
export function getIntervalMidpoint(interval: Interval): Date {
  return addMilliseconds(
    interval.start,
    differenceInMilliseconds(interval.end, interval.start) / 2,
  );
}

/**
 * Whether `interval.end` has already passed `referenceDate`. Assumes a
 * temporally-ordered interval (start <= end) — validate with
 * `isValidInterval` first if the input isn't already trusted (e.g. an
 * `ActivityModel.plannedTimes`, which is).
 */
export function isIntervalFullyInPast(
  interval: Interval,
  referenceDate = new Date(),
): boolean {
  return isBefore(interval.end, referenceDate);
}
